import React from 'react';
import { Link } from '../../atoms/Link';
import { Text } from '../../atoms/Text';
import { List } from '../../atoms/List';
import { ListItem } from '../../atoms/ListItem';
import { Image } from '../../atoms/Image';
import homeIcon from '../../assets/images/icon_house.png';
import separatorIcon from '../../assets/images/bullet_breadcrumb.png';
import './Breadcrumbs.css';

export interface BreadcrumbItem {
  /**
   * URL for the breadcrumb link
   */
  url: string;
  /**
   * Display text for the breadcrumb
   */
  title?: string;
  /**
   * Whether this breadcrumb should not be clickable
   */
  unclickable?: boolean;
}

export interface BreadcrumbsProps {
  /**
   * Array of breadcrumb items to display
   */
  items: BreadcrumbItem[];
  /**
   * Presales domain for external links (e.g., 'www.heartinternet.uk')
   */
  wwwDomain?: string;
  /**
   * Optional CSS class name
   */
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  wwwDomain: _wwwDomain = 'www.heartinternet.uk',
  className = '',
}) => {
  // Don't render if no items or only one item
  if (!items || items.length <= 1) {
    return null;
  }

  const firstItem = items[0];

  return (
    <nav
      id="hi-ui-breadcrumbs"
      className={`hi-ui hi-ui-breadcrumbs tw-w-full tw-py-4 ${className}`.trim()}
      aria-label="Breadcrumb"
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <List
          variant="ordered"
          listStyle="none"
          className="tw-flex tw-flex-wrap tw-items-center tw-gap-0"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Link href={firstItem.url}>
            <Image src={homeIcon} alt="Home" className="tw-inline-block" />
          </Link>
          {items.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === items.length - 1;
            const isClickable = !isLast && !item.unclickable;

            return (
              <React.Fragment key={`${item.url}-${index}`}>
                {!isFirst && <Image src={separatorIcon} alt="" className="tw-mx-2" />}
                <ListItem className="tw-flex tw-items-center tw-text-sm tw-text-[#2B2B2B]">
                  {isClickable ? (
                    <Link
                      href={item.url}
                      className="tw-text-[#2B2B2B] hover:tw-text-[#2B2B2B] hover:tw-underline tw-no-underline"
                    >
                      {item.title || item.url}
                    </Link>
                  ) : (
                    <Text
                      as="span"
                      inline
                      className={isLast ? 'tw-break-words' : ''}
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {item.title || item.url}
                    </Text>
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </div>
    </nav>
  );
};
